import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { ResponsiveImage, IconSpinner } from '..';
import { propTypes } from '../../util/types';

import css from './ImageCarousel.css';

const KEY_CODE_LEFT_ARROW = 37;
const KEY_CODE_RIGHT_ARROW = 39;

const PortfolioImageCarousel = props => {
  const {
    rootClassName,
    className,
    images,
    getImageVariants,
    dynamicImageData,
    intl,
    initialIndex = 0,
  } = props;

  const [selectedImageIndex, setSelectedImageIndex] = useState(initialIndex);
  const [selectedImageLoaded, setSelectedImageLoaded] = useState(false);

  const count = images.length;

  const prev = useCallback(() => {
    if (count < 2) return;
    setSelectedImageIndex(prevIndex => (count > 0 ? (count + prevIndex - 1) % count : 0));
    setSelectedImageLoaded(false);
  }, [count]);

  const next = useCallback(() => {
    if (count < 2) return;
    setSelectedImageIndex(prevIndex => (count > 0 ? (count + prevIndex + 1) % count : 0));
    setSelectedImageLoaded(false);
  }, [count]);

  const onKeyUp = useCallback(
    e => {
      if (e.keyCode === KEY_CODE_LEFT_ARROW) {
        prev();
      } else if (e.keyCode === KEY_CODE_RIGHT_ARROW) {
        next();
      }
    },
    [prev, next]
  );

  useEffect(() => {
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [onKeyUp]);

  const markImageLoaded = index => () => {
    if (selectedImageIndex === index) {
      setSelectedImageLoaded(true);
    }
  };

  // Reset index if images or initialIndex changes
  useEffect(() => {
    setSelectedImageIndex(initialIndex);
  }, [initialIndex, images]);

  const classes = classNames(rootClassName || css.root, className);

  const naturalIndex = selectedImageIndex + 1;
  const imageIndex =
    images.length > 0 ? (
      <span className={css.imageIndex}>
        {naturalIndex}/{images.length}
      </span>
    ) : null;
  const prevButton = images.length > 1 ? <button className={css.prev} onClick={prev} /> : null;
  const nextButton = images.length > 1 ? <button className={css.next} onClick={next} /> : null;

  const imageAltText = intl.formatMessage(
    {
      id: 'ImageCarousel.imageAltText',
    },
    {
      index: naturalIndex,
      count: images.length,
    }
  );

  const currentImageIsLoaded = images.length === 0 || selectedImageLoaded;
  const loadingIconClasses = classNames(css.loading, {
    [css.loadingVisible]: !currentImageIsLoaded,
  });
  const imageClasses = classNames(css.image, {
    [css.imageLoading]: !currentImageIsLoaded,
  });

  return (
    <div className={classes}>
      <div className={css.imageWrapper}>
        <IconSpinner className={loadingIconClasses} />
        <ResponsiveImage
          className={imageClasses}
          alt={imageAltText}
          image={images[selectedImageIndex]}
          onLoad={markImageLoaded(selectedImageIndex)}
          onError={markImageLoaded(selectedImageIndex)}
          variants={getImageVariants(images[selectedImageIndex], dynamicImageData)}
          sizes="(max-width: 767px) 100vw, 80vw"
        />
      </div>
      {imageIndex}
      {prevButton}
      {nextButton}
    </div>
  );
};

PortfolioImageCarousel.defaultProps = {
  rootClassName: null,
  className: null,
  initialIndex: 0,
};

const { string, arrayOf } = PropTypes;

PortfolioImageCarousel.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
  initialIndex: PropTypes.number,
};

export default injectIntl(PortfolioImageCarousel);
